<template>
  <div>
    <div
      class="db-tw-flex db-tw-gap-4 db-tw-my-3 db-tw-flex-wrap db-wrapper-radio-icon-container-small-mobile"
      id="db-wrapper-radio-icon-container"
    >
      <div
        v-for="(item, j) in !showAll && !!visibleItems ? visibleItems : checkbox"
        :key="j"
        class="db-wrapper-icon"
      >
        <DbRadioIcon
          :modelValue="modelValue"
          :inputAttrs="{
            value: checkbox[j].cle || checkbox[j].name,
            name: 'room',
          }"
          v-on:update:model-value="$emit('update:modelValue', $event)"
        >
          <template v-slot:icon>
            <!--
              Icons will come from the backoffice as HTML strings
              We wrap them inside .db-icon to mimic other icons
            -->
            <!-- eslint-disable vue/no-v-html -->
            <span
              class="db-icon"
              v-html="item.default_icon"
            />
            <!-- eslint-enable vue/no-v-html -->
          </template>

          <div
            v-if="item.subText"
            class="db-function-options-label"
          >
            <p :class="['db-label', { 'db-selected': item.id === modelValue }]">
              {{ item.name }}
            </p>
            <p
              v-if="item.subText"
              class="db-price"
            >
              <span>{{ item.subText }}</span>
            </p>
          </div>
          <span v-else>{{ item.name }}</span>
        </DbRadioIcon>
      </div>
    </div>
    <button
      v-if="checkbox.length && checkbox.length > visibleItems.length"
      type="button"
      v-on:click="toggleShowAll"
      class="db-wrapper-icon-more db-button-text-link"
    >
      {{ !showAll ? $t('Show all options') : $t('Show less options') }}
      <IconDbChevron
        width="10"
        height="10"
        class="db-chevron"
        :class="{
          'db-rotate-90': showAll,
          'db-rotate-270': !showAll,
        }"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import type { RoomSetupOptions } from '@/features/RfpForm/components/RfpGroupRoomSetup/RfpGroupRoomSetupTypes'
import { onBeforeUnmount, nextTick, onMounted, ref } from 'vue'

const showAll = ref(false)
const visibleItems = ref<RoomSetupOptions>([])

const props = withDefaults(
  defineProps<{
    modelValue?: string
    checkbox: RoomSetupOptions
  }>(),
  {
    modelValue: '',
    isMandatory: false,
  },
)

defineEmits<{
  (e: 'update:modelValue', modelValue: string): void
}>()

const computeVisibleItems = () => {
  const container = document.getElementById('db-wrapper-radio-icon-container')
  const containerWidth = container?.offsetWidth
  const childWidth = container?.firstElementChild?.offsetWidth + 8 * 2 || 189 // Add padding (default value is 189)
  // If invalid, put 3 element by default
  if (!container || !containerWidth || !childWidth) {
    visibleItems.value = props.checkbox.slice(0, 3)
    return
  }
  let totalWidth = 0
  visibleItems.value = []

  for (const item of props.checkbox) {
    if (totalWidth + childWidth > containerWidth) break
    visibleItems.value.push(item)
    totalWidth += childWidth
  }
  // Add at least three element
  if (visibleItems.value.length <= 2) {
    visibleItems.value = props.checkbox.slice(0, 3)
  }
}

onMounted(() => {
  window.addEventListener('resize', computeVisibleItems)
  nextTick(() => {
    computeVisibleItems()
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', computeVisibleItems)
})

const toggleShowAll = () => {
  showAll.value = !showAll.value
}
</script>

<style scoped>
.db-app .db-wrapper-icon {
  background-color: var(--db-color-background-radioicon-text);
  border-radius: 4px;
  box-shadow: 0 0 0 1px var(--db-color-border-radioicon);
  margin-left: 1px;
  position: relative;
  height: 117px;
  width: 104px;
}

@media screen and (min-width: 992px) {
  .db-app .db-wrapper-icon {
    height: 182px;
    width: 173px;
  }
}

@media screen and (max-width: 365px) {
  .db-app .db-wrapper-radio-icon-container-small-mobile {
    gap: 3px !important;
  }
}

@media screen and (min-width: 366px) and (max-width: 370px) {
  .db-app .db-wrapper-radio-icon-container-small-mobile {
    gap: 6px !important;
  }
}

@media screen and (min-width: 371px) and (max-width: 380px) {
  .db-app .db-wrapper-radio-icon-container-small-mobile {
    gap: 8px !important;
  }
}

@media screen and (min-width: 381px) and (max-width: 390px) {
  .db-app .db-wrapper-radio-icon-container-small-mobile {
    gap: 10px !important;
  }
}

.db-app .db-wrapper-icon:hover {
  box-shadow: 0 0 0 1px var(--db-color-border-radioicon-hover);
}

.db-app .db-wrapper-icon:focus {
  box-shadow: 0 0 0 1px var(--db-color-border-radioicon-focus);
}

.db-app .db-wrapper-icon:has(.db-radio-icon:checked) {
  box-shadow: 0 0 0 1px var(--db-color-border-radioicon-checked);
}

.db-wrapper-radio-icon :deep(.db-label) {
  width: 100%;
}

.db-app .db-function-options-label p {
  padding: var(--db-spacer-2) 0;
  font-size: var(--db_small_body_text_font_size);
}

.db-app .db-function-options-label .db-label.db-selected {
  font-weight: 700;
}

.db-app .db-function-options-label .db-price {
  color: var(--db_rate_color);
  border-top: 1px solid var(--db_borders_color);
}

.db-app .db-wrapper-icon-more {
  font-family: var(--db_body_text_font_family);
  font-size: var(--db_body_text_font_size);
  color: var(--db_link_color, #0ba7db);
}

.db-app .db-wrapper-icon-more:hover {
  color: var(--db_link_hover_color, #0ba7db);
}
</style>
